import * as React from "react";
import { useState, memo } from "react";
import { useDispatch } from "react-redux";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { cn } from "@/lib/utils";
import { useThemeContext } from "@/assets/theme/ThemeContextProvider";
import { useTheme } from "@/providers/theme-provider";
import { authAction } from "@/redux/actions";
import { HugeiconsIcon } from "@hugeicons/react";
import { ChevronRightIcon, SettingsIcon } from "@/components/ui/icons";
import {
  ComputerIcon,
  CustomerSupportIcon,
  Globe02Icon,
  Logout01Icon,
  Moon02Icon,
  Sun01Icon,
  Tick02Icon,
} from "@hugeicons/core-free-icons";
import { useSession } from "@/providers/session-provider";

// ---------------------------------------------------------------------------
// Theme segmented control (Light / Dark / System) — replaces ThemeSwitcher.
// Style modelled on the Chat / Cowork / Code segmented control.
// ---------------------------------------------------------------------------
const THEMES = [
  { key: "light", label: "Light", icon: Sun01Icon },
  { key: "dark", label: "Dark", icon: Moon02Icon },
  { key: "system", label: "System", icon: ComputerIcon },
] as const;

type ThemeKey = (typeof THEMES)[number]["key"];

function ThemeSegmented({
  value,
  onChange,
}: {
  value: ThemeKey;
  onChange: (next: ThemeKey) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-0.5 p-0.5 bg-[var(--gray-100)] dark:bg-[var(--gray-950)] rounded-md">
      {THEMES.map(({ key, label, icon }) => {
        const isActive = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            aria-pressed={isActive}
            className={cn(
              "flex items-center justify-center gap-1.5 h-7 rounded-sm text-[12px] font-medium transition-all cursor-pointer",
              isActive
                ? "bg-background text-foreground shadow-xs border border-border dark:bg-[var(--gray-800)] dark:border-transparent"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <HugeiconsIcon icon={icon} size={14} strokeWidth={1.75} />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic menu row — a clickable item in the popover body.
// Margin-x leaves a gutter so the rounded hover state floats inside the card.
// ---------------------------------------------------------------------------
function MenuRow({
  icon,
  label,
  onClick,
  href,
  trailing,
  variant = "default",
}: {
  icon?: React.ComponentProps<typeof HugeiconsIcon>["icon"];
  label: string;
  onClick?: () => void;
  href?: string;
  trailing?: React.ReactNode;
  variant?: "default" | "destructive";
}) {
  const className = cn(
    "w-full flex items-center gap-3 px-2.5 py-2 rounded-md text-left text-sm transition-colors cursor-pointer no-underline",
    variant === "destructive"
      ? "text-foreground hover:bg-destructive/10 hover:text-destructive [&_svg]:text-muted-foreground hover:[&_svg]:text-destructive"
      : "text-foreground hover:bg-accent hover:text-accent-foreground [&_svg]:text-muted-foreground hover:[&_svg]:text-accent-foreground",
  );
  const inner = (
    <>
      {icon && (
        <HugeiconsIcon
          icon={icon}
          size={16}
          strokeWidth={1.75}
          className="shrink-0 text-muted-foreground"
        />
      )}
      <span className="flex-1">{label}</span>
      {trailing}
    </>
  );

  if (href) {
    return (
      <a href={href} className={className} onClick={onClick}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Language sub-menu — opens as a side popover. Visual only for now.
// ---------------------------------------------------------------------------
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
];

function LanguageMenu() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string>("en");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "group w-full flex items-center gap-3 px-2.5 py-2 rounded-md text-left text-sm transition-colors cursor-pointer text-foreground",
            "hover:bg-accent hover:text-accent-foreground",
            "[&_svg]:text-muted-foreground hover:[&_svg]:text-accent-foreground",
            open && "bg-accent text-accent-foreground [&_svg]:text-accent-foreground",
          )}
        >
          <HugeiconsIcon
            icon={Globe02Icon}
            size={16}
            strokeWidth={1.75}
            className="shrink-0"
          />
          <span className="flex-1">Language</span>
          <HugeiconsIcon icon={ChevronRightIcon} size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-44 p-1.5 shadow-xl border-[var(--border-strong)]"
      >
        <div className="flex flex-col">
          {LANGUAGES.map((lang) => {
            const isActive = selected === lang.code;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => {
                  setSelected(lang.code);
                  setOpen(false);
                }}
                className={cn(
                  "w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-sm transition-colors cursor-pointer",
                  isActive
                    ? "text-primary font-medium"
                    : "text-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <span className="flex-1">{lang.label}</span>
                {isActive && (
                  <HugeiconsIcon
                    icon={Tick02Icon}
                    size={16}
                    strokeWidth={2}
                    className="text-primary"
                  />
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Main settings popover content
// ---------------------------------------------------------------------------
const SettingsContent = memo(
  ({
    handleClose,
    requestLogout,
  }: {
    user_role: any;
    handleClose: () => void;
    requestLogout: () => void;
    handleSupportAction: (type: string) => void;
  }) => {
    const { theme, setTheme } = useTheme();
    const { session } = useSession();
    const { changeMode } = useThemeContext();

    const currentTheme: ThemeKey =
      (theme as ThemeKey) ||
      ((localStorage.getItem("themeType") as ThemeKey | null) ?? "system");

    const handleThemeChange = (next: ThemeKey) => {
      if (localStorage.getItem("themeType")) {
        localStorage.setItem("themeType", next);
      }
      setTheme(next);
      changeMode(next);
    };

    return (
      <div className="flex flex-col">
        {/* Profile header — blue → indigo → purple gradient curve (the original)
            bumped to ~30% opacity so the colours read clearly on white. */}
        <div className="rounded-t-[inherit] bg-gradient-to-br from-blue-500/25 via-indigo-500/25 to-purple-500/25 dark:from-blue-500/30 dark:via-indigo-500/30 dark:to-purple-500/30 px-4 py-3.5 border-b border-[var(--border-subtle,var(--border))]">
          <div className="flex items-center gap-3">
            <Avatar className="size-10 shrink-0">
              <AvatarImage
                src={session?.avatar_filename || ""}
                alt={session?.first_name || session?.email || ""}
              />
              <AvatarFallback tone="blue">
                {(
                  session?.first_name?.[0] ||
                  session?.email?.[0] ||
                  "U"
                ).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-foreground truncate">
                {`${session?.first_name || ""} ${session?.last_name || ""}`.trim() ||
                  session?.email ||
                  "User"}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {session?.email || ""}
              </div>
            </div>
          </div>
        </div>

        {/* Body — consistent 6px outer gutter on all sides so the rounded
            hover rectangles float evenly inside the dropdown. */}
        <div className="flex flex-col px-1.5 py-1.5">
          {/* Theme segmented control — first item, no label */}
          <div className="px-1 py-1">
            <ThemeSegmented value={currentTheme} onChange={handleThemeChange} />
          </div>

          {/* Divider */}
          <div className="h-px bg-border my-1.5 mx-1" />

          {/* Primary actions */}
          <MenuRow
            icon={SettingsIcon}
            label="Settings"
            href="/settings"
            onClick={handleClose}
          />
          <MenuRow
            icon={CustomerSupportIcon}
            label="Support"
            href="/settings?tab=support"
            onClick={handleClose}
          />

          {/* Divider */}
          <div className="h-px bg-border my-1.5 mx-1" />

          {/* Logout — opens a confirmation alert */}
          <MenuRow
            icon={Logout01Icon}
            label="Log out"
            variant="destructive"
            onClick={() => {
              handleClose();
              requestLogout();
            }}
          />
        </div>
      </div>
    );
  },
);

SettingsContent.displayName = "SettingsContent";

// ---------------------------------------------------------------------------
// Public wrapper
// ---------------------------------------------------------------------------
export const SettingsPopover = ({ children }: React.PropsWithChildren) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  const logout = () => {
    localStorage.removeItem("staySignInAG");
    dispatch(authAction.logout() as any);
  };

  const handleSupportAction = (actionType: string = "") => {
    setOpen(false);
    const gleap = (window as any)?.Gleap;
    if (actionType === "help") {
      gleap?.openHelpCenter();
    } else if (actionType === "chat") {
      gleap?.openConversations();
    } else {
      gleap?.open();
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div>{children}</div>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 p-0 shadow-2xl border-[var(--border-strong)] rounded-xl overflow-hidden"
          align="end"
          sideOffset={10}
          side="bottom"
        >
          <SettingsContent
            user_role={"user"}
            handleClose={() => setOpen(false)}
            requestLogout={() => setConfirmLogoutOpen(true)}
            handleSupportAction={handleSupportAction}
          />
        </PopoverContent>
      </Popover>

      {/* Logout confirmation */}
      <ConfirmActionDialog
        open={confirmLogoutOpen}
        onOpenChange={setConfirmLogoutOpen}
        icon={Logout01Icon}
        title="Log out of Flozy?"
        description="You'll need to sign in again to access your workspace."
        confirmLabel="Log out"
        onConfirm={() => {
          setConfirmLogoutOpen(false);
          logout();
        }}
      />
    </>
  );
};
